import { useEffect, useState } from 'react';
import { FieldValues, useController } from 'react-hook-form';
import styled from 'styled-components';

import { Input } from '@/components/Forms';
import Text from '@/components/Text';
import { api } from '@/utils/api';
import { splitMatchedText } from '@/utils/helpers';

import { DefaultProps, TextFieldProps } from '../FormField';

const Wrapper = styled.div`
  position: relative;
`;

const Autocomplete = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  padding: 8px 4px;
  max-height: 368px;
  overflow-y: auto;
  left: 0;
  width: 100%;
  z-index: 1;
`;

const AutocompleteButton = styled.button`
  padding: 9px 12px;
  background-color: ${({ theme }) => theme.colors.white};
  transition: 0.2s ease-out all;
  line-height: 22px;
  width: 100%;
  text-align: left;
  border-radius: 4px;
  outline: none;
  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.colors.grayscaleLight};
    ${Text} {
      &:first-child {
        color: ${({ theme }) => theme.colors.black};
      }
    }
  }
  ${Text} {
    transition: color ease-out 0.2s;
    > span {
      color: ${({ theme }) => theme.colors.purple};
    }
  }
`;

const MaxChars = styled(Text)`
  margin-top: 8px;
`;

type Props<T extends FieldValues> = Omit<
  DefaultProps<T> & TextFieldProps,
  'type'
>;

const TextField = <T extends FieldValues>({
  name,
  control,
  inputType,
  suggestionsEndpoint,
  maximumCharacters,
  ...rest
}: Props<T>) => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (!field.value || !suggestionsEndpoint) {
      setSuggestions([]);
      return;
    }

    if (!showSuggestions) return;

    const timer = setTimeout(
      () =>
        api
          .get<never, string[]>(suggestionsEndpoint, {
            params: {
              'filter[search]': field.value,
            },
          })
          .then(setSuggestions),
      500,
    );

    return () => {
      clearTimeout(timer);
    };
  }, [field.value, suggestionsEndpoint, showSuggestions]);

  const onSuggestionClick = (value: string) => field.onChange(value);

  return (
    <Wrapper>
      <Input
        {...field}
        {...rest}
        $hasError={!!error}
        type={inputType}
        onFocus={() => setShowSuggestions(true)}
        onBlur={() => {
          setShowSuggestions(false);
          setSuggestions([]);
        }}
      />
      {!!maximumCharacters && (
        <MaxChars $color="gray" $type="extraSmall">
          Maximum {maximumCharacters} characters
        </MaxChars>
      )}
      {showSuggestions && !!suggestions.length && (
        <Autocomplete>
          {suggestions.map((s) => (
            <AutocompleteButton
              key={s}
              type="button"
              as="button"
              onMouseDown={() => {
                onSuggestionClick(s);
                setShowSuggestions(false);
                setSuggestions([]);
              }}
            >
              <Text
                $color="gray"
                $type="captionSemibold"
                dangerouslySetInnerHTML={{
                  __html: splitMatchedText(s, field.value),
                }}
              />
            </AutocompleteButton>
          ))}
        </Autocomplete>
      )}
    </Wrapper>
  );
};

export default TextField;
